import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, MapPin, Quote, Star } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';
import { IMAGES, STATS, CONCEPT, STATIONS, CLUBS, TEAM, TESTIMONIALS } from '../data/mock';
import GoogleReviews from '../components/GoogleReviews';
import TrialCTA from '../components/TrialCTA';

function Hero() {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scrollScale, setScrollScale] = useState(1);

  // Stronger parallax than before
  const handleMouseMove = useCallback((e) => {
    const x = (e.clientX / window.innerWidth - 0.5) * 40;
    const y = (e.clientY / window.innerHeight - 0.5) * 40;
    setOffset({ x, y });
  }, []);

  // Athlete zooms in as you scroll down (comes closer), zooms back out scrolling up
  const handleScroll = useCallback(() => {
    const y = Math.min(window.scrollY, 700);
    setScrollScale(1 + y * 0.0009);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleMouseMove, handleScroll]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden hx-grain bg-[#0a0a0a]">
      {/* Accent glow behind athlete */}
      <div className="absolute right-[2%] top-1/2 -translate-y-1/2 w-[55%] h-[80%] bg-[radial-gradient(circle,rgba(205,255,0,0.10),transparent_65%)] hidden lg:block pointer-events-none" />

      {/* Athlete image (PNG on black blends with hero) */}
      <div
        className="absolute inset-0 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[64%] pointer-events-none"
        style={{ transform: `translate(${offset.x * -1}px, ${offset.y * -1}px)`, transition: 'transform 0.25s ease-out' }}
      >
        <img
          src={IMAGES.heroAthlete}
          alt="Athlète Hyrox poussant un traîneau"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover object-top opacity-95 will-change-transform"
          style={{ transform: `scale(${scrollScale})`, transformOrigin: 'center 30%', transition: 'transform 0.15s linear' }}
        />
        {/* Blend edges into the dark background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent lg:via-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
      </div>

      {/* Floating accent shapes */}
      <div className="absolute right-[8%] top-[18%] w-40 h-40 border border-accent/25 rounded-full hidden md:block hx-float" />
      <div className="absolute right-[14%] bottom-[20%] w-3 h-3 bg-gold rounded-full hidden md:block hx-float" style={{ animationDelay: '1.5s' }} />


      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full pt-28">
        <div className="max-w-3xl">
          <p className="hx-eyebrow mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-accent" /> Toulouse · Colomiers
          </p>
          <h1 className="font-display text-[18vw] sm:text-[12vw] lg:text-[9.5rem] leading-[0.82] drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)]">
            BUILD<br />
            FOR <span className="text-accent">HYROX</span>
          </h1>
          <p className="text-[var(--hx-muted)] text-lg md:text-xl max-w-xl mt-7 leading-relaxed">
            La salle d'Hyrox qui combine course et fonctionnel. Deux salles, une communauté.
            <span className="text-white font-medium"> À partir de 29€ / mois</span> pour un accès dans tes deux salles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <Link to="/reserver" className="hx-btn text-base justify-center" data-cta="home-hero-reserver">
              Réserver une séance <ArrowRight size={18} />
            </Link>
            <Link to="/lhyrox" className="hx-btn hx-btn-outline text-base justify-center">
              C'est quoi l'Hyrox ?
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 z-10">
        <span className="font-cond text-[10px] tracking-[0.3em] text-[var(--hx-muted)]">SCROLL</span>
        <div className="w-5 h-9 border border-[#333] rounded-full grid place-items-start p-1">
          <span className="w-1 h-1.5 bg-accent rounded-full hx-scrolldot" />
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ['HYROX', 'CROSSFIT', 'FONCTIONNEL', 'COMMUNAUTÉ', 'PERFORMANCE', 'COACHING'];
  const row = [...items, ...items];
  return (
    <div className="bg-accent py-3 overflow-hidden border-y border-accent">
      <div className="hx-marquee-track hx-marquee-fast">
        {row.map((t, i) => (
          <span key={`${t}-${i}`} className="font-display text-black text-xl px-6 flex items-center gap-6">
            {t} <Star size={14} fill="#000" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Stats() {
  return (
    <section className="bg-[#0d0d0d] border-b border-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 80} className="py-10 md:py-14 text-center border-r border-[#1a1a1a] last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r">
            <p className="font-display text-5xl md:text-6xl text-accent">{s.value}{s.suffix}</p>
            <p className="font-cond text-xs tracking-widest text-[var(--hx-muted)] mt-2">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Concept() {
  return (
    <section className="py-16 md:py-32 max-w-7xl mx-auto px-5 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <p className="hx-eyebrow mb-4">{CONCEPT.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">{CONCEPT.title}</h2>
          <p className="text-[var(--hx-muted)] text-lg leading-relaxed">{CONCEPT.text}</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-5">
          {CONCEPT.cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 120}>
              <Link to={c.to} className="hx-card group block h-full overflow-hidden rounded-md">
                <div className="h-44 overflow-hidden">
                  <img src={IMAGES[c.img]} alt={c.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-6">
                  <h3 className="font-cond text-xl tracking-wide mb-2 group-hover:text-accent transition-colors">{c.title}</h3>
                  <p className="text-[var(--hx-muted)] text-sm leading-relaxed mb-4">{c.text}</p>
                  <span className="inline-flex items-center gap-1 text-accent font-cond text-sm tracking-wider">
                    Découvrir <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stations() {
  return (
    <section className="py-16 md:py-32 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="hx-eyebrow mb-4">Le format Hyrox</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] max-w-xl">8 stations. 1 chrono. 0 excuse.</h2>
          </div>
          <p className="text-[var(--hx-muted)] max-w-sm">8 km de course entrecoupés de 8 stations fonctionnelles. Le test ultime de ta forme physique.</p>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATIONS.map((s, i) => (
            <Reveal key={s.name} delay={(i % 4) * 80}>
              <div className="hx-card group relative h-56 rounded-md overflow-hidden">
                <img src={IMAGES[s.img]} alt={s.name} loading="lazy" decoding="async" className="w-full h-full object-cover opacity-60 transition-all duration-700 group-hover:opacity-90 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-between">
                  <span className="font-display text-2xl text-accent">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-cond text-lg tracking-wide leading-tight">{s.name}</h3>
                    <p className="text-[var(--hx-muted)] text-xs mt-1 max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500">{s.desc}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Clubs() {
  const clubs = Object.values(CLUBS);
  return (
    <section className="py-16 md:py-32 max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal className="text-center mb-14">
        <p className="hx-eyebrow mb-4">Nos clubs d'Hyrox</p>
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Deux villes, une communauté</h2>
        <p className="text-[var(--hx-muted)] mt-4">… et bientôt une 3e ville.</p>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6">
        {clubs.map((c, i) => (
          <Reveal key={c.slug} delay={i * 120}>
            <Link to={`/club/${c.slug}`} className="hx-card group relative block h-[420px] rounded-md overflow-hidden">
              <img src={IMAGES[c.hero]} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <p className="hx-eyebrow mb-2 flex items-center gap-2"><MapPin size={14} />{c.city}</p>
                <h3 className="font-display text-4xl mb-2 group-hover:text-accent transition-colors">{c.name}</h3>
                <p className="text-[var(--hx-muted)] mb-4">{c.tagline}</p>
                <span className="inline-flex items-center gap-1 text-accent font-cond tracking-wider text-sm">
                  Voir la salle <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function DoubleAbo() {
  return (
    <section className="py-16 md:py-28 bg-[#0d0d0d] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <span className="inline-block bg-accent text-black font-display text-xl px-3 py-1 mb-5 -rotate-2">OFFERT</span>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95] mb-5">Ton accès Hyrox offert</h2>
          <p className="text-[var(--hx-muted)] text-lg leading-relaxed mb-7">
            Nos salles proposent aussi la méthodologie CrossFit. Ton accès Hyrox est <span className="text-white font-medium">offert</span> si tu es abonné CrossFit. Tu attends quoi ?
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/reserver" className="hx-btn justify-center">Mon double abo à Toulouse</Link>
            <Link to="/reserver" className="hx-btn hx-btn-outline justify-center">Mon double abo à Colomiers</Link>
          </div>
        </Reveal>
        <Reveal delay={150} className="order-1 lg:order-2">
          <div className="relative">
            <img src={IMAGES.facility4} alt="Salle CrossFit & Hyrox" loading="lazy" decoding="async" className="rounded-md w-full h-[380px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-accent text-black p-5 rounded-md hidden sm:block">
              <p className="font-display text-3xl">2 EN 1</p>
              <p className="font-cond text-xs tracking-widest">HYROX + CROSSFIT</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TeamPreview() {
  return (
    <section className="py-16 md:py-32 max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="hx-eyebrow mb-4">Rencontre l'équipe</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Mêmes passions,<br />différents parcours</h2>
        </div>
        <Link to="/equipe" className="hx-btn hx-btn-outline self-start md:self-end">Toute l'équipe <ArrowRight size={16} /></Link>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {TEAM.map((m, i) => (
          <Reveal key={m.name} delay={(i % 4) * 80}>
            <div className="hx-card group rounded-md overflow-hidden">
              <div className="h-72 overflow-hidden relative">
                <img src={IMAGES[m.img]} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 to-transparent" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-2xl group-hover:text-accent transition-colors">{m.name}</h3>
                <p className="font-cond text-xs tracking-widest text-accent">{m.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-16 md:py-32 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="text-center mb-14">
          <p className="hx-eyebrow mb-4">Ils en parlent</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">La communauté Hybrid</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 120}>
              <div className="hx-card rounded-md p-7 h-full flex flex-col">
                <Quote size={32} className="text-accent mb-4" />
                <p className="text-[var(--hx-text)] leading-relaxed mb-6 flex-1">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={IMAGES[t.img]} alt={t.name} loading="lazy" decoding="async" className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-cond tracking-wide">{t.name}</p>
                    <p className="text-[var(--hx-muted)] text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Stats />
      <Concept />
      <Stations />
      <Clubs />
      <DoubleAbo />
      <TeamPreview />
      <Testimonials />
      <GoogleReviews />
      <TrialCTA />
    </>
  );
}
